import React, { useMemo } from 'react';
import { LazyImage } from '@teko/ui';
import { htmlDecode } from '@teko/utils';
import { styled } from '@teko/ds-desktop';
import { SpaceProps } from 'styled-system';
import { useTenantConfig } from 'hooks';
import DOMPurify from 'dompurify';

interface NotificationItemProps extends SpaceProps {
  title: string;
  message: string;
  time: string;
  isRead?: boolean;
  avatar?: string;
  type?: string;
  onClick?: () => void;
}

interface StyledNotificationItemProps {
  isRead?: boolean;
}

export const NotificationItem: React.FC<NotificationItemProps> = (
  props: NotificationItemProps,
) => {
  const { epicOmni948, logo } = useTenantConfig();
  const date = props.time ? new Date(props.time) : new Date();
  const formatDate = `${date.getDate()}/${
    date.getMonth() + 1
  } - ${date.getHours()}:${date.getMinutes()}`;
  const avatarImgPath = useMemo(() => {
    return epicOmni948
      ? props.avatar || logo.full.path || ''
      : props.avatar || '';
  }, [epicOmni948, props.avatar, logo.full.path]);
  return (
    <Wrapper isRead={props.isRead} {...props}>
      <Avatar>
        {!!avatarImgPath && (
          <ProductImageWrapper>
            <LazyImage src={avatarImgPath} alt="" borderRadius={50} contain />
          </ProductImageWrapper>
        )}
      </Avatar>
      <ContentWrapper>
        <HeaderWrapper>
          <TitleWrapper>{props.title}</TitleWrapper>
          {props.time && <TimeWrapper>{formatDate}</TimeWrapper>}
        </HeaderWrapper>
        {props.message.includes('\n') ? (
          <>
            {props.message.split('\n').map((item, index) => (
              <MessageWrapper
                key={`${item}-${index}`}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(htmlDecode(item)),
                }}
              />
            ))}
          </>
        ) : (
          <MessageWrapper
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(htmlDecode(props.message)),
            }}
          />
        )}
      </ContentWrapper>
    </Wrapper>
  );
};

const borderColor = '#e0e0e0';

const Wrapper = styled.div<StyledNotificationItemProps>(
  ({ isRead }: StyledNotificationItemProps) => `
  padding: 16px;
  display: flex;
  cursor: pointer;
  border-bottom: 1px solid ${borderColor};
  background-color: ${isRead ? 'white' : 'rgb(255, 242, 242)'};
`,
);

const Avatar = styled.div`
  position: relative;
  margin-right: 16px;
  width: 64px;
  height: 64px;
  border: 1px solid ${borderColor};
  border-radius: 50%;
`;

const ProductImageWrapper = styled.div`
  background-color: unset;
  border-radius: 50%;
`;

const ContentWrapper = styled.div`
  width: calc(100% - 80px);
  .message {
    font-size: 14px;
    color: #848788;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-height: 18px;
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitleWrapper = styled.span`
  font-size: 13px;
  font-weight: 500;
  line-height: 24px;
  color: #434657;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin-right: 8px;
`;

const TimeWrapper = styled.span`
  font-size: 12px;
  color: #82869e;
  line-height: 16px;
  min-width: fit-content;
`;

const MessageWrapper = styled.span`
  font-size: 13px;
  color: #82869e;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-height: 20px;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;
